/**
 * HostActionBridge — always-mounted handler for `wg:host_app_action`
 * messages (the bootstrap relay of `WunderGuide.send(verb, args)` from
 * deep-integration partner pages).
 *
 * Lives at the App level, NOT in ChatView: views are conditionally
 * mounted, and a listener inside ChatView dies the moment the visitor is
 * on the Trip/checkout view — host adds silently dropped (the "Trip tab
 * not syncing" bug). Gated per instance by feature_flags.allow_host_actions.
 *
 * `submit_message` is re-posted through the existing wg:queued_submit
 * path so ChatView's dedup/session-gating applies unchanged.
 */
import { useEffect, useRef } from 'react';
import { executeAppAction, type ActionDeps } from '../chat/actions';
import { useChatSession } from '../context/ChatSessionContext';
import { useCheckout } from '../context/CheckoutContext';
import { useInstance } from '../context/InstanceContext';
import { useParentBridge } from '../context/ParentBridgeContext';
import { useGatedAddItem } from '../hooks/useGatedAddItem';
import { useRemoveItineraryByRef } from '../hooks/useRemoveItineraryByRef';
import { useItinerary } from '../context/ItineraryContext';
import { useView } from '../context/ViewContext';
import { initiateBookingForSession } from '../api/checkoutApi';
import { httpClient } from '../client';

export default function HostActionBridge() {
  const { config } = useInstance();
  const { chatSessionId: sessionId } = useChatSession();
  const { view: currentView, setView } = useView();
  const { setCart, setPrefillContact } = useCheckout();
  // Auth-gated: guest adds are queued and AuthView opens (registration
  // converts the guest, then the queue flushes). See useGatedAddItem.
  const addItineraryItem = useGatedAddItem();
  const removeItineraryItem = useRemoveItineraryByRef();
  const { shareItinerary } = useItinerary();
  const parentBridge = useParentBridge();

  const depsRef = useRef<ActionDeps | null>(null);
  depsRef.current = {
    // submit_message is intercepted below and rerouted through
    // wg:queued_submit, so this never fires — present for the type.
    onSendMessage: () => {},
    setView,
    setCart,
    setPrefillContact,
    addItineraryItem,
    removeItineraryItem,
    shareItinerary,
    parentBridge: {
      send: parentBridge.send,
      requestClose: parentBridge.requestClose,
      dispatchAction: parentBridge.dispatchAction,
      requestLayout: parentBridge.requestLayout,
      layoutMax: parentBridge.layoutMax,
    },
    currentView,
    buildCheckoutFromProduct: sessionId
      ? body => {
          const itemId = `${body.provider_id}_${body.product_id}`;
          return initiateBookingForSession(
            sessionId,
            itemId,
            body,
            httpClient,
          ).then(r => ({
            booking_type: r.booking_type,
            url: r.url,
            detection_patterns: r.detection_patterns,
          }));
        }
      : undefined,
  };

  const enabled = !!config?.feature_flags?.allow_host_actions;
  useEffect(() => {
    if (!enabled) return;
    const onMsg = (ev: MessageEvent) => {
      const data = ev?.data as
        | {
            type?: string;
            payload?: { verb?: string; args?: Record<string, unknown> };
          }
        | undefined;
      if (!data || data.type !== 'wg:host_app_action') return;
      const verb = data.payload?.verb;
      const args = data.payload?.args ?? {};
      const deps = depsRef.current;
      if (!verb || !deps) return;
      if (verb === 'submit_message') {
        window.postMessage(
          { type: 'wg:queued_submit', payload: { text: args.text } },
          '*',
        );
        return;
      }
      executeAppAction(verb, args, deps);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [enabled]);

  return null;
}
